import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Activity as ActivityIcon,
  Bot,
  Search,
  Shield,
  Ticket,
  Users as UsersIcon,
} from 'lucide-react';
import type { AdminActivityQuery, AdminAiUsageQuery, AdminUsersQuery } from '../../shared/api';
import type { AiGenerationKind } from '../../shared/types';
import { AI_GENERATION_KINDS } from '../../shared/types';
import { formatBytes } from '../../shared/utils';
import { PageHeader } from '../components/layout';
import {
  Avatar,
  Badge,
  Button,
  Card,
  EmptyState,
  Input,
  Select,
  Skeleton,
  StatCard,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui';
import { api, ApiError } from '../lib/api';
import { qk } from '../lib/query';
import { formatDay, formatNumber, formatWhen } from '../lib/format';
import { debounce } from '../lib/utils';

/* ================================================================== */
/* Overview                                                             */
/* ================================================================== */

function OverviewPanel() {
  const query = useQuery({ queryKey: qk.adminOverview(), queryFn: () => api.admin.overview() });

  if (query.isLoading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (query.isError || !query.data) {
    return (
      <EmptyState
        icon={<Shield className="size-5" />}
        title="Could not load the overview"
        description={query.error instanceof ApiError ? query.error.message : 'Try refreshing the page.'}
      />
    );
  }

  const { stats, workspaces, pendingInvites } = query.data;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <StatCard label="Users" value={formatNumber(stats.users)} />
        <StatCard label="Workspaces" value={formatNumber(stats.workspaces)} />
        <StatCard label="Posts" value={formatNumber(stats.posts)} hint={`${formatNumber(stats.published)} published`} />
        <StatCard label="Scheduled" value={formatNumber(stats.scheduled)} />
        <StatCard
          label="Media stored"
          value={formatNumber(stats.mediaAssets)}
          hint={formatBytes(stats.mediaBytes)}
        />
        <StatCard label="AI calls (24h)" value={formatNumber(stats.aiCalls24h)} />
        <StatCard
          label="AI errors (24h)"
          value={formatNumber(stats.aiErrors24h)}
          hint={stats.aiErrors24h > 0 ? 'Check AI usage' : 'All clear'}
        />
        <StatCard
          label="Publish failures (24h)"
          value={formatNumber(stats.publishFailures24h)}
          hint={stats.publishFailures24h > 0 ? 'Check activity' : 'All clear'}
        />
      </div>

      <div className="space-y-3">
        <h2 className="text-sm font-semibold text-fg">Workspaces</h2>
        {workspaces.length === 0 ? (
          <p className="text-sm text-muted">No workspaces yet.</p>
        ) : (
          <Card className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs text-subtle">
                  <th className="px-4 py-2.5 font-medium">Workspace</th>
                  <th className="px-4 py-2.5 font-medium">Members</th>
                  <th className="px-4 py-2.5 font-medium">Posts</th>
                  <th className="px-4 py-2.5 font-medium">Connected</th>
                  <th className="px-4 py-2.5 font-medium">Created</th>
                </tr>
              </thead>
              <tbody>
                {workspaces.map((workspace) => (
                  <tr key={workspace.id} className="border-b border-border last:border-0">
                    <td className="px-4 py-2.5">
                      <p className="font-medium text-fg">{workspace.name}</p>
                      <p className="text-xs text-subtle">/{workspace.slug}</p>
                    </td>
                    <td className="pap-tnum px-4 py-2.5 text-muted">{workspace.memberCount}</td>
                    <td className="pap-tnum px-4 py-2.5 text-muted">{workspace.postCount}</td>
                    <td className="px-4 py-2.5 text-muted">
                      {workspace.connectedPlatforms.length === 0 ? (
                        <span className="text-subtle">None</span>
                      ) : (
                        workspace.connectedPlatforms.join(', ')
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-muted">{formatDay(workspace.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </div>

      <div className="space-y-3">
        <h2 className="text-sm font-semibold text-fg">Pending invites</h2>
        {pendingInvites.length === 0 ? (
          <p className="text-sm text-muted">No open invitations across the deployment.</p>
        ) : (
          <Card className="divide-y divide-border">
            {pendingInvites.map((invite) => (
              <div key={invite.id} className="flex flex-wrap items-center gap-3 px-4 py-2.5 text-sm">
                <Ticket className="size-3.5 shrink-0 text-subtle" aria-hidden />
                <span className="min-w-0 flex-1 truncate text-fg">{invite.email}</span>
                <span className="text-muted">{invite.workspaceName}</span>
                <Badge tone="neutral" size="sm" className="capitalize">
                  {invite.role}
                </Badge>
                <span className="text-xs text-subtle">Expires {formatDay(invite.expiresAt)}</span>
              </div>
            ))}
          </Card>
        )}
      </div>
    </div>
  );
}

/* ================================================================== */
/* Users                                                                */
/* ================================================================== */

function UsersPanel() {
  const [search, setSearch] = useState('');
  const [searchText, setSearchText] = useState('');
  const [cursors, setCursors] = useState<Array<string | undefined>>([undefined]);
  const [pageIndex, setPageIndex] = useState(0);

  const commitSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearch(value);
        setCursors([undefined]);
        setPageIndex(0);
      }, 300),
    [],
  );

  const cursor = cursors[pageIndex];
  const query = useQuery({
    queryKey: [...qk.adminUsers(search), cursor ?? 'first'],
    queryFn: () => api.admin.users({ search: search || undefined, cursor, limit: 25 } satisfies AdminUsersQuery),
    placeholderData: (previous) => previous,
  });

  const users = query.data?.users ?? [];
  const nextCursor = query.data?.nextCursor ?? null;

  return (
    <div className="space-y-4">
      <Input
        aria-label="Search users"
        inputSize="sm"
        leadingIcon={<Search className="size-3.5" />}
        placeholder="Search by name or email…"
        value={searchText}
        onChange={(event) => {
          setSearchText(event.target.value);
          commitSearch(event.target.value);
        }}
        wrapperClassName="max-w-sm"
      />

      {query.isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : users.length === 0 ? (
        <EmptyState icon={<UsersIcon className="size-5" />} title="No users found" />
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs text-subtle">
                <th className="px-4 py-2.5 font-medium">User</th>
                <th className="px-4 py-2.5 font-medium">Workspaces</th>
                <th className="px-4 py-2.5 font-medium">Last active</th>
                <th className="px-4 py-2.5 font-medium">Joined</th>
              </tr>
            </thead>
            <tbody>
              {users.map((row) => (
                <tr key={row.id} className="border-b border-border last:border-0">
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2.5">
                      <Avatar name={row.name} email={row.email} src={row.avatarUrl} size="sm" />
                      <div className="min-w-0">
                        <p className="flex items-center gap-1.5 truncate font-medium text-fg">
                          {row.name ?? row.email}
                          {row.isSuperadmin && (
                            <Badge tone="accent" size="sm">
                              Superadmin
                            </Badge>
                          )}
                        </p>
                        <p className="truncate text-xs text-subtle">{row.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-muted">
                    {row.workspaces.length === 0 ? (
                      <span className="text-subtle">None</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {row.workspaces.map((ws) => (
                          <Badge key={ws.id} tone="neutral" size="sm">
                            {ws.name}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-muted">
                    {row.lastActiveAt ? formatWhen(row.lastActiveAt) : <span className="text-subtle">Never</span>}
                  </td>
                  <td className="px-4 py-2.5 text-muted">{formatDay(row.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {(pageIndex > 0 || nextCursor) && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-subtle">Page {pageIndex + 1}</p>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={pageIndex === 0}
              onClick={() => setPageIndex((i) => Math.max(0, i - 1))}
            >
              Previous
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={!nextCursor}
              onClick={() => {
                if (!nextCursor) return;
                setCursors((history) =>
                  pageIndex + 1 < history.length ? history : [...history, nextCursor],
                );
                setPageIndex((i) => i + 1);
              }}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ================================================================== */
/* Activity                                                             */
/* ================================================================== */

function ActivityPanel() {
  const [action, setAction] = useState('');
  const [cursors, setCursors] = useState<Array<string | undefined>>([undefined]);
  const [pageIndex, setPageIndex] = useState(0);

  const cursor = cursors[pageIndex];
  const query = useQuery({
    queryKey: [...qk.adminActivity(), action, cursor ?? 'first'],
    queryFn: () =>
      api.admin.activity({ action: action || undefined, cursor, limit: 40 } satisfies AdminActivityQuery),
    placeholderData: (previous) => previous,
  });

  const entries = query.data?.entries ?? [];
  const nextCursor = query.data?.nextCursor ?? null;

  return (
    <div className="space-y-4">
      <Input
        aria-label="Filter by action"
        inputSize="sm"
        placeholder="Filter by action, e.g. post.published"
        value={action}
        onChange={(event) => {
          setAction(event.target.value);
          setCursors([undefined]);
          setPageIndex(0);
        }}
        wrapperClassName="max-w-sm"
      />

      {query.isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : entries.length === 0 ? (
        <EmptyState icon={<ActivityIcon className="size-5" />} title="No activity recorded yet" />
      ) : (
        <Card className="divide-y divide-border">
          {entries.map((entry) => (
            <div key={entry.id} className="flex items-start gap-3 px-4 py-2.5 text-sm">
              <Avatar
                name={entry.user?.name}
                email={entry.user?.email}
                src={entry.user?.avatarUrl}
                size="sm"
              />
              <div className="min-w-0 flex-1">
                <p className="text-fg">
                  <span className="font-medium">{entry.user?.name ?? entry.user?.email ?? 'System'}</span>{' '}
                  <span className="text-muted">{entry.action}</span>
                  {entry.targetType && <span className="text-subtle"> · {entry.targetType}</span>}
                </p>
                <p className="text-xs text-subtle">{formatWhen(entry.createdAt)}</p>
              </div>
            </div>
          ))}
        </Card>
      )}

      {(pageIndex > 0 || nextCursor) && (
        <div className="flex justify-end gap-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={pageIndex === 0}
            onClick={() => setPageIndex((i) => Math.max(0, i - 1))}
          >
            Previous
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={!nextCursor}
            onClick={() => {
              if (!nextCursor) return;
              setCursors((history) => (pageIndex + 1 < history.length ? history : [...history, nextCursor]));
              setPageIndex((i) => i + 1);
            }}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}

/* ================================================================== */
/* AI usage                                                             */
/* ================================================================== */

const KIND_OPTIONS = [
  { value: '', label: 'All kinds' },
  ...AI_GENERATION_KINDS.map((kind) => ({ value: kind, label: kind.replace('_', ' ') })),
];

function AiUsagePanel() {
  const [kind, setKind] = useState<AiGenerationKind | ''>('');
  const [cursors, setCursors] = useState<Array<string | undefined>>([undefined]);
  const [pageIndex, setPageIndex] = useState(0);

  const cursor = cursors[pageIndex];
  const query = useQuery({
    queryKey: [...qk.adminAiUsage(kind || undefined), cursor ?? 'first'],
    queryFn: () =>
      api.admin.aiUsage({ kind: kind || undefined, cursor, limit: 40 } satisfies AdminAiUsageQuery),
    placeholderData: (previous) => previous,
  });

  const generations = query.data?.generations ?? [];
  const nextCursor = query.data?.nextCursor ?? null;
  const totals = query.data?.totals;

  return (
    <div className="space-y-4">
      {totals && (
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <StatCard label="Calls" value={formatNumber(totals.calls)} />
            <StatCard label="Errors" value={formatNumber(totals.errors)} />
            <StatCard label="Avg duration" value={`${formatNumber(Math.round(totals.avgDurationMs))} ms`} />
          </div>
          <div className="flex flex-wrap items-center gap-4 text-xs text-muted">
            <span className="flex flex-wrap items-center gap-1.5">
              <span className="font-medium text-subtle">By provider:</span>
              {totals.byProvider.map((p) => (
                <Badge key={p.provider} tone={p.provider === 'mock' ? 'warning' : 'neutral'} size="sm">
                  {p.provider} · {p.count}
                </Badge>
              ))}
            </span>
            <span className="flex flex-wrap items-center gap-1.5">
              <span className="font-medium text-subtle">By kind:</span>
              {totals.byKind.map((k) => (
                <Badge key={k.kind} tone="neutral" size="sm">
                  {k.kind.replace('_', ' ')} · {k.count}
                </Badge>
              ))}
            </span>
          </div>
        </div>
      )}

      <Select
        aria-label="Filter by kind"
        selectSize="sm"
        wrapperClassName="max-w-xs"
        options={KIND_OPTIONS}
        value={kind}
        onChange={(event) => {
          setKind(event.target.value as AiGenerationKind | '');
          setCursors([undefined]);
          setPageIndex(0);
        }}
      />

      {query.isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : generations.length === 0 ? (
        <EmptyState icon={<Bot className="size-5" />} title="No AI generations recorded yet" />
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs text-subtle">
                <th className="px-4 py-2.5 font-medium">Kind</th>
                <th className="px-4 py-2.5 font-medium">Model</th>
                <th className="px-4 py-2.5 font-medium">Provider</th>
                <th className="px-4 py-2.5 font-medium">Status</th>
                <th className="px-4 py-2.5 font-medium">Duration</th>
                <th className="px-4 py-2.5 font-medium">When</th>
              </tr>
            </thead>
            <tbody>
              {generations.map((generation) => (
                <tr key={generation.id} className="border-b border-border last:border-0">
                  <td className="px-4 py-2.5 capitalize text-fg">{generation.kind.replace('_', ' ')}</td>
                  <td className="px-4 py-2.5 text-muted">{generation.model}</td>
                  <td className="px-4 py-2.5 text-muted">
                    <Badge tone={generation.provider === 'mock' ? 'warning' : 'neutral'} size="sm">
                      {generation.provider}
                    </Badge>
                  </td>
                  <td className="px-4 py-2.5">
                    <Badge tone={generation.status === 'ok' ? 'success' : 'danger'} size="sm" dot>
                      {generation.status}
                    </Badge>
                  </td>
                  <td className="pap-tnum px-4 py-2.5 text-muted">
                    {generation.durationMs !== null ? `${formatNumber(generation.durationMs)} ms` : '—'}
                  </td>
                  <td className="px-4 py-2.5 text-muted">{formatWhen(generation.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {(pageIndex > 0 || nextCursor) && (
        <div className="flex justify-end gap-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={pageIndex === 0}
            onClick={() => setPageIndex((i) => Math.max(0, i - 1))}
          >
            Previous
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={!nextCursor}
            onClick={() => {
              if (!nextCursor) return;
              setCursors((history) => (pageIndex + 1 < history.length ? history : [...history, nextCursor]));
              setPageIndex((i) => i + 1);
            }}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}

/* ================================================================== */
/* Page                                                                 */
/* ================================================================== */

/**
 * Admin — owned by WEB-4, superadmin only (route-gated in App.tsx). Overview
 * stats, users, activity log and AI usage — clean, data-dense tables.
 */
export function AdminPage() {
  const [tab, setTab] = useState('overview');

  return (
    <>
      <PageHeader
        title="Admin"
        description="System-wide view: users, workspaces, invites, AI usage and activity."
      />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="overview">
            <Shield className="size-3.5" /> Overview
          </TabsTrigger>
          <TabsTrigger value="users">
            <UsersIcon className="size-3.5" /> Users
          </TabsTrigger>
          <TabsTrigger value="activity">
            <ActivityIcon className="size-3.5" /> Activity
          </TabsTrigger>
          <TabsTrigger value="ai-usage">
            <Bot className="size-3.5" /> AI usage
          </TabsTrigger>
        </TabsList>
        <TabsContent value="overview">
          <OverviewPanel />
        </TabsContent>
        <TabsContent value="users">
          <UsersPanel />
        </TabsContent>
        <TabsContent value="activity">
          <ActivityPanel />
        </TabsContent>
        <TabsContent value="ai-usage">
          <AiUsagePanel />
        </TabsContent>
      </Tabs>
    </>
  );
}

export default AdminPage;
